/**
 * Daily snapshot for trending products/content carousels.
 *
 * Computes ranked post IDs once per calendar day (per params), then reuses
 * that set so we skip the expensive analytics RPC on subsequent loads.
 * Mid-day threshold crossings are intentionally delayed until tomorrow.
 *
 * Layers: in-memory → localStorage → component row (post_ids + query marker).
 * Stale rows (yesterday / rolled window) still paint immediately; a background
 * refresh updates caches + the component row without blocking first paint.
 */
import dayjs from "dayjs";
import { supabase } from "../../../config/supabaseClient";
import { getComponentsTableName } from "../../componentsTable";
import {
  fetchTrendingClickAnalytics,
  trendingPostIdsFromAnalyticsRows,
} from "../../SupabaseServices/trendingClickAnalytics";
import {
  getTemplateContentPostIds,
  getTemplateShoppablePostIds,
} from "../../SupabaseServices/TemplateAnalyticsDatabase";
import { trendingWindowToInclusiveShopRange } from "../../../Screens/DashboardView/MasterTemplateEditor/websiteEditorTrendingDetails";

const SNAP_QUERY_PREFIX = "__trending_daily__";
const LS_PREFIX = "trendingDailySnap:v1:";

/**
 * `legacy` — link-in-bio `analytics` (Post Click + shop redirect).
 * `template-shop` — website shop clicks, same as Top shopped.
 * `template-content` — website post clicks, same as Top viewed.
 * Link-in-bio stays on legacy. Website products and content opt in by type.
 */
export type TrendingClickSource = "legacy" | "template-shop" | "template-content";

export function trendingClickSource(args: {
  from?: string | null;
  type?: string | null;
  /** Component.query — Website LIB rows are landing-page surface but store
   *  template-shop/content snapshot markers from Master Template create. */
  query?: string | null;
}): TrendingClickSource {
  const surface = String(args.from || "").trim().toLowerCase();
  const type = String(args.type || "").trim().toLowerCase();
  // Snapshot query embeds `|template-shop` / `|template-content` in paramsKey.
  // Website LIB uses landing-page `from` but Master Template create writes those
  // markers — honor them so live load matches Add preview (not legacy analytics).
  const snapQuery = String(args.query || "");
  if (snapQuery.includes("|template-shop")) return "template-shop";
  if (snapQuery.includes("|template-content")) return "template-content";
  if (surface !== "website-template") return "legacy";
  if (type === "trending products" || type === "trending-products") {
    return "template-shop";
  }
  if (type === "trending content" || type === "trending-content") {
    return "template-content";
  }
  return "legacy";
}

export type TrendingSnapshotParams = {
  userId: string;
  username: string;
  numberOfClicks: number | string;
  startDate: string;
  endDate: string;
  type?: string;
  clickSource?: TrendingClickSource;
};

export type TrendingSnapshotPersist = {
  componentId?: string | number | null;
  from?: string | null;
  /** Component.query — may already hold today's snapshot marker */
  query?: string | null;
  /** Component.post_ids — may already hold today's ranked ids */
  postIds?: string | null;
};

export type TrendingSnapshotSource =
  | "memory"
  | "localStorage"
  | "database"
  | "computed";

type SnapshotRecord = {
  date: string;
  paramsKey: string;
  postIds: string[];
};

const memoryCache = new Map<string, SnapshotRecord>();

/** Writes in flight so concurrent first-loads don't stampede the analytics RPC. */
const inflight = new Map<string, Promise<string[]>>();

export function todayLocalYYYYMMDD(): string {
  return dayjs().format("YYYY-MM-DD");
}

export function buildTrendingParamsKey(params: TrendingSnapshotParams): string {
  const parts = [
    String(params.userId || "").trim(),
    String(params.numberOfClicks ?? ""),
    String(params.startDate || "").trim(),
    String(params.endDate || "").trim(),
    String(params.type || "").toLowerCase().trim(),
  ];
  // Suffix only website sources so existing link-in-bio snapshot keys stay valid.
  if (
    params.clickSource === "template-shop" ||
    params.clickSource === "template-content"
  ) {
    parts.push(params.clickSource);
  }
  return parts.join("|");
}

function memoryKey(paramsKey: string, date: string): string {
  return `${paramsKey}::${date}`;
}

function lsKey(paramsKey: string): string {
  return `${LS_PREFIX}${paramsKey}`;
}

export function encodeTrendingSnapshotQuery(
  date: string,
  paramsKey: string,
): string {
  return `${SNAP_QUERY_PREFIX}:${date}:${paramsKey}`;
}

export function parseTrendingSnapshotQuery(
  query?: string | null,
): { date: string; paramsKey: string } | null {
  const raw = String(query || "").trim();
  if (!raw.startsWith(SNAP_QUERY_PREFIX + ":")) return null;
  const rest = raw.slice(SNAP_QUERY_PREFIX.length + 1);
  const firstColon = rest.indexOf(":");
  if (firstColon <= 0) return null;
  const date = rest.slice(0, firstColon);
  const paramsKey = rest.slice(firstColon + 1);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date) || !paramsKey) return null;
  return { date, paramsKey };
}

function parsePostIdsList(value?: string | null): string[] {
  if (!value) return [];
  if (Array.isArray(value)) {
    return value.map((id) => String(id).trim()).filter(Boolean);
  }
  return String(value)
    .split(",")
    .map((id) => id.trim())
    .filter(Boolean);
}

function seedClientCaches(record: SnapshotRecord): void {
  memoryCache.set(memoryKey(record.paramsKey, record.date), record);
  writeLocalStorage(record);
}

/** Drop a cached miss so the next load retries analytics instead of sticking empty. */
function clearClientCaches(paramsKey: string, date: string): void {
  memoryCache.delete(memoryKey(paramsKey, date));
  if (typeof localStorage === "undefined") return;
  try {
    localStorage.removeItem(lsKey(paramsKey));
  } catch {
    // ignore
  }
}

function writeSnapshotCaches(record: SnapshotRecord): void {
  if (record.postIds.length > 0) {
    seedClientCaches(record);
  } else {
    clearClientCaches(record.paramsKey, record.date);
  }
}

/**
 * Build the initial trending snapshot at component create time — same idea as
 * custom links storing `post_ids` on insert. Prefer IDs already loaded in the
 * create preview; otherwise compute from analytics once.
 */
export async function createTrendingPostIdsSnapshot(args: {
  userId: string;
  username: string;
  numberOfClicks: number | string;
  startDate: string;
  endDate: string;
  type: string;
  preferredPostIds?: string[] | string | null;
  clickSource?: TrendingClickSource;
}): Promise<{ postIds: string; query: string; ids: string[] }> {
  const params: TrendingSnapshotParams = {
    userId: args.userId,
    username: args.username,
    numberOfClicks: args.numberOfClicks,
    startDate: args.startDate,
    endDate: args.endDate,
    type: args.type,
    clickSource: args.clickSource,
  };
  const paramsKey = buildTrendingParamsKey(params);
  const date = todayLocalYYYYMMDD();

  let ids = parsePostIdsList(args.preferredPostIds as any);
  if (!ids.length) {
    ids = await computeTrendingPostIdsFromAnalytics({
      userId: args.userId,
      username: args.username,
      startDate: args.startDate,
      endDate: args.endDate,
      numberOfClicks: args.numberOfClicks,
      clickSource: args.clickSource,
    });
  }

  const record: SnapshotRecord = { date, paramsKey, postIds: ids };
  // Never cache an empty miss — a flaky / cold RPC would stick "No data" for
  // the rest of the day for this params key (custom date toggles looked broken).
  writeSnapshotCaches(record);

  return {
    postIds: ids.join(","),
    query: encodeTrendingSnapshotQuery(date, paramsKey),
    ids,
  };
}

function readLocalStorage(paramsKey: string): SnapshotRecord | null {
  if (typeof localStorage === "undefined") return null;
  try {
    const raw = localStorage.getItem(lsKey(paramsKey));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as SnapshotRecord;
    if (
      !parsed ||
      typeof parsed.date !== "string" ||
      !Array.isArray(parsed.postIds) ||
      parsed.paramsKey !== paramsKey
    ) {
      return null;
    }
    return {
      date: parsed.date,
      paramsKey: parsed.paramsKey,
      postIds: parsed.postIds.map(String).filter(Boolean),
    };
  } catch {
    return null;
  }
}

function writeLocalStorage(record: SnapshotRecord): void {
  if (typeof localStorage === "undefined") return;
  try {
    localStorage.setItem(lsKey(record.paramsKey), JSON.stringify(record));
  } catch {
    // Quota / private mode — ignore.
  }
}

function readDbSnapshot(
  persist: TrendingSnapshotPersist | undefined,
  paramsKey: string,
  today: string,
): { ids: string[]; fresh: boolean } | null {
  if (!persist) return null;
  const ids = parsePostIdsList(persist.postIds);
  if (!ids.length) return null;
  const marker = parseTrendingSnapshotQuery(persist.query);
  // Snapshot must match today's date AND analytics params (min clicks + window).
  // Same-day edits that only change min clicks must not reuse yesterday's ids.
  if (marker?.date === today && marker.paramsKey === paramsKey) {
    return { ids, fresh: true };
  }
  // Params changed today (e.g. min-clicks edit) — force a recompute.
  if (marker?.date === today && marker.paramsKey !== paramsKey) {
    return null;
  }
  // Stale but usable — paint immediately; caller refreshes in background.
  return { ids, fresh: false };
}

/** Kick a once-per-key analytics recompute without blocking first paint. */
function scheduleTrendingRefresh(args: {
  memKey: string;
  params: TrendingSnapshotParams;
  paramsKey: string;
  today: string;
  persist?: TrendingSnapshotPersist;
}): void {
  if (inflight.has(args.memKey)) return;

  const computePromise = (async () => {
    const postIds = await computeTrendingPostIdsFromAnalytics({
      userId: args.params.userId,
      username: args.params.username,
      startDate: args.params.startDate,
      endDate: args.params.endDate,
      numberOfClicks: args.params.numberOfClicks,
      clickSource: args.params.clickSource,
    });
    const record: SnapshotRecord = {
      date: args.today,
      paramsKey: args.paramsKey,
      postIds,
    };
    writeSnapshotCaches(record);
    void persistSnapshotToComponent(
      args.persist,
      args.params.userId,
      record,
    );
    return postIds;
  })();

  inflight.set(args.memKey, computePromise);
  void computePromise.finally(() => {
    inflight.delete(args.memKey);
  });
}

/**
 * Persist today's ranked ids onto the component row once per day.
 * Fire-and-forget; RLS may block anonymous public writes — local cache still helps.
 */
async function persistSnapshotToComponent(
  persist: TrendingSnapshotPersist | undefined,
  userId: string,
  record: SnapshotRecord,
): Promise<void> {
  const componentId = persist?.componentId;
  if (componentId == null || componentId === "") return;
  // template_components / stayuptodate ids are bigint — never PATCH with a title slug.
  const idStr = String(componentId).trim();
  if (!/^\d+$/.test(idStr)) {
    return;
  }

  const marker = parseTrendingSnapshotQuery(persist?.query);
  // Already stored for today with same params — skip DB write.
  if (
    marker &&
    marker.date === record.date &&
    marker.paramsKey === record.paramsKey &&
    parsePostIdsList(persist?.postIds).join(",") === record.postIds.join(",")
  ) {
    return;
  }

  const table = getComponentsTableName(persist?.from || undefined);
  try {
    await supabase
      .from(table)
      .update({
        post_ids: record.postIds.join(","),
        query: encodeTrendingSnapshotQuery(record.date, record.paramsKey),
      })
      .eq("id", idStr)
      .eq("user_id", userId)
      .neq("status", "deleted");
  } catch {
    // RLS / network — local cache still helps.
  }
}

/**
 * Ranked post IDs from the trending analytics RPC (the slow path).
 * Uses shared v2 click analytics (post_id merge + min clicks).
 */
export async function computeTrendingPostIdsFromAnalytics(args: {
  userId: string;
  username: string;
  startDate: string;
  endDate: string;
  numberOfClicks: number | string;
  clickSource?: TrendingClickSource;
}): Promise<string[]> {
  if (args.clickSource === "template-shop") {
    const range = trendingWindowToInclusiveShopRange(
      args.startDate,
      args.endDate,
    );
    if (!range.startDate || !range.endDate) return [];
    return getTemplateShoppablePostIds({
      username: args.username,
      userId: args.userId,
      startDate: range.startDate,
      endDate: range.endDate,
      minClicks: args.numberOfClicks,
      max: 5000,
    });
  }

  if (args.clickSource === "template-content") {
    const range = trendingWindowToInclusiveShopRange(
      args.startDate,
      args.endDate,
    );
    if (!range.startDate || !range.endDate) return [];
    return getTemplateContentPostIds({
      username: args.username,
      userId: args.userId,
      startDate: range.startDate,
      endDate: range.endDate,
      minClicks: args.numberOfClicks,
      max: 5000,
    });
  }

  const analytics = await fetchTrendingClickAnalytics({
    username: args.username,
    userId: args.userId,
    startDate: args.startDate,
    endDate: args.endDate,
    minClicks: args.numberOfClicks,
    maxRes: 5000,
  });

  if (analytics.analyticsRpc === "none") {
    return [];
  }

  return trendingPostIdsFromAnalyticsRows(analytics.rows);
}

/**
 * Resolve trending post IDs for today — snapshot hit skips the analytics RPC.
 */
export async function getOrComputeTrendingPostIds(args: {
  params: TrendingSnapshotParams;
  persist?: TrendingSnapshotPersist;
  bypassSnapshot?: boolean;
  label?: string;
}): Promise<{ postIds: string[]; source: TrendingSnapshotSource }> {
  const today = todayLocalYYYYMMDD();
  const paramsKey = buildTrendingParamsKey(args.params);
  const memKey = memoryKey(paramsKey, today);

  const finish = (
    postIds: string[],
    source: TrendingSnapshotSource,
  ): { postIds: string[]; source: TrendingSnapshotSource } => {
    return { postIds, source };
  };

  if (!args.bypassSnapshot) {
    const mem = memoryCache.get(memKey);
    if (
      mem &&
      mem.date === today &&
      mem.paramsKey === paramsKey &&
      mem.postIds.length > 0
    ) {
      return finish(mem.postIds, "memory");
    }

    const ls = readLocalStorage(paramsKey);
    if (ls && ls.date === today && ls.postIds.length > 0) {
      memoryCache.set(memKey, ls);
      return finish(ls.postIds, "localStorage");
    }

    const dbSnap = readDbSnapshot(args.persist, paramsKey, today);
    if (dbSnap?.fresh && dbSnap.ids.length > 0) {
      const record: SnapshotRecord = {
        date: today,
        paramsKey,
        postIds: dbSnap.ids,
      };
      memoryCache.set(memKey, record);
      writeLocalStorage(record);
      return finish(dbSnap.ids, "database");
    }

    // Stale snapshot (yesterday / rolled date window): paint now, refresh later.
    // This is the common "sometimes slow" path — analytics is ~2s on large accounts.
    const staleIds =
      (ls && ls.postIds.length > 0 ? ls.postIds : null) ||
      (dbSnap?.ids?.length ? dbSnap.ids : null) ||
      null;

    if (staleIds) {
      const provisional: SnapshotRecord = {
        date: today,
        paramsKey,
        postIds: staleIds,
      };
      // Provisional today entry so remounts / next page don't re-enter analytics.
      memoryCache.set(memKey, provisional);
      writeLocalStorage(provisional);
      scheduleTrendingRefresh({
        memKey,
        params: args.params,
        paramsKey,
        today,
        persist: args.persist,
      });
      return finish(staleIds, ls?.postIds?.length ? "localStorage" : "database");
    }
  }

  // Deduplicate concurrent computes for the same key.
  const existing = inflight.get(memKey);
  if (existing) {
    const postIds = await existing;
    return finish(postIds, "computed");
  }

  const computePromise = (async () => {
    const postIds = await computeTrendingPostIdsFromAnalytics({
      userId: args.params.userId,
      username: args.params.username,
      startDate: args.params.startDate,
      endDate: args.params.endDate,
      numberOfClicks: args.params.numberOfClicks,
      clickSource: args.params.clickSource,
    });

    const record: SnapshotRecord = {
      date: today,
      paramsKey,
      postIds,
    };
    writeSnapshotCaches(record);
    // Once per day (or when params change) — update component row.
    void persistSnapshotToComponent(
      args.persist,
      args.params.userId,
      record,
    );
    return postIds;
  })();

  inflight.set(memKey, computePromise);
  try {
    const postIds = await computePromise;
    return finish(postIds, "computed");
  } finally {
    inflight.delete(memKey);
  }
}
